import axios from 'axios';

/* Directory client (listings, people, authors and the domain's directory
   map). Mirrors api.js -- same base URL, same Bearer token -- with its own
   axios instance, like apiEmails.js. */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const apiDirectory = axios.create({ baseURL: API_BASE_URL });

apiDirectory.interceptors.request.use(
  (config) => {
    let token = null;
    try {
      token = localStorage.getItem('access_token');
    } catch {
      token = null;
    }
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

const base = (domainId, collection) => `/domain/${encodeURIComponent(domainId)}/${collection}`;

/** GET /domain/{id}/directory -> {enabled, metro, version, vertical, vocab, counts} */
export const getDirectory = async (domainId) => (await apiDirectory.get(base(domainId, 'directory'))).data;

/** PUT /domain/{id}/directory with any of {enabled, metro, vertical, vocab}. */
export const updateDirectory = async (domainId, body) => (await apiDirectory.put(base(domainId, 'directory'), body)).data;

/** GET /domain/{id}/{collection}?q&city_slug&status&thin&limit&cursor -> {items, next_cursor, total}
    Listing items carry thin: {thin, reasons, score}; thin=true lists thin ones only. */
export const listDirectory = async (domainId, collection, { q, citySlug, status, thin, limit = 50, cursor } = {}) => {
  const response = await apiDirectory.get(base(domainId, collection), {
    params: {
      q: q || undefined,
      city_slug: citySlug || undefined,
      status: status || undefined,
      thin: thin ? true : undefined,
      limit,
      cursor: cursor || undefined,
    },
  });
  return response.data;
};

export const getDirectoryItem = async (domainId, collection, slug) => (
  await apiDirectory.get(`${base(domainId, collection)}/${encodeURIComponent(slug)}`)
).data;

export const createDirectoryItem = async (domainId, collection, body) => (
  await apiDirectory.post(base(domainId, collection), body)
).data;

export const updateDirectoryItem = async (domainId, collection, slug, body) => (
  await apiDirectory.put(`${base(domainId, collection)}/${encodeURIComponent(slug)}`, body)
).data;

export const deleteDirectoryItem = async (domainId, collection, slug) => (
  await apiDirectory.delete(`${base(domainId, collection)}/${encodeURIComponent(slug)}`)
).data;

/** POST /domain/{id}/directory/photos {media_id, slug} -> {name, url} */
export const copyDirectoryPhoto = async (domainId, mediaId, slug) => (
  await apiDirectory.post(`${base(domainId, 'directory')}/photos`, { media_id: mediaId, slug })
).data;

/** FastAPI 422s carry a list of {loc, msg}; turn any error into one line. */
export const errorText = (err, fallback = 'Request failed') => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => {
      const loc = (d.loc || []).filter((p) => p !== 'body').join('.');
      const msg = String(d.msg || '').replace(/^Value error, /, '');
      return loc ? `${loc}: ${msg}` : msg;
    }).join('; ');
  }
  return err?.message || fallback;
};
